import { NetworkStatus } from "@apollo/client/core";
import * as Sentry from "@sentry/nextjs";
import { Masonry } from "masonic";
import Image from "next/image";
import { useRouter } from "next/router";
import React, { ReactNode, useMemo } from "react";
import { Waypoint } from "react-waypoint";

import {
  ErrorState,
  Loading,
} from "@/features/common/components/informationContainer";
import { Container } from "@/features/common/components/layout/Container";
import { Square } from "@/features/common/components/square/Square";
import { useMasonry } from "@/features/common/hooks/masonry/useMasonry";
import { PostCard } from "@/features/post/components/postCard/PostCard";
import { ImageCardProps } from "@/features/post/components/postCard/type";
import { validatePost } from "@/features/post/components/utils/validatePost";
import ProfileMasonryPlaceholder from "@/features/user/components/ProfileMasonryPlaceholder";
import {
  QueryPostPageArgs,
  usePostPageQuery,
} from "@/generated/generated-hooks";
import { getPublicConfig } from "@/helpers/getPublicConfig";

import { EmptyPostPage } from "./EmptyPostPage";
import { EmptyPostPlaceholder } from "./EmptyPostPlaceholder";
import { PostPlaceholder } from "./PostPlaceholder";
import { MasonryOpenSeaAssetsProvider } from "./utils/InfiniteMasonryReducer";

const config = getPublicConfig();

type InfiniteMasonryProps = {
  postPageVariables: Omit<QueryPostPageArgs, "paging">;
  about?: ReactNode;
  isProfileOwner?: boolean;
};

export const InfiniteMasonry: React.FC<InfiniteMasonryProps> = (props) => {
  const { postPageVariables, about, isProfileOwner = false } = props;
  const router = useRouter();

  const {
    mode,
    scrollToIndex,
    onRender,
    renderIndex,
    onClickGridItem,
    settings,
  } = useMasonry();

  const { data, loading, error, fetchMore, variables, networkStatus } =
    usePostPageQuery({
      variables: {
        ...postPageVariables,
        paging: { limit: config.defaultPageSize },
      },
      skip: !postPageVariables,
      ssr: false,
      notifyOnNetworkStatusChange: true,
      errorPolicy: "all",
    });

  const fetchMoreLoading = loading && networkStatus === NetworkStatus.fetchMore;
  const gridMode = mode === "grid";
  const desktopMode = mode === "desktop";

  const waypointLoadMore = async () => {
    if (!data) return;
    const { hasNext, next } = data.postPage.paging;
    if (loading || !hasNext || !next) return;

    fetchMore({
      variables: { ...variables, paging: { ...variables.paging, next } },
    }).catch((e) =>
      Sentry.captureException(e, {
        tags: {
          component: "InfiniteMasonry",
          fn: "usePostPageQuery.fetchMore",
        },
      }),
    );
  };

  const targetDataStructure: ImageCardProps[] = useMemo(() => {
    if (!data || !data.postPage) return [];

    const showEmptyPostPlaceholder =
      isProfileOwner && data.postPage.items.length === 0;
    if (showEmptyPostPlaceholder) return [{ post: null }];
    const validItems = data.postPage.items.filter(validatePost);

    const items = validItems.map((post) => {
      return { post };
    });

    return items;
  }, [data, isProfileOwner]);

  const masonryKey = useMemo(() => {
    return (
      `${mode}${router.route}${isProfileOwner}` +
      JSON.stringify(postPageVariables)
    );
  }, [mode, router.route, postPageVariables, isProfileOwner]);

  if (!data || (loading && !fetchMoreLoading)) {
    if (gridMode) {
      return <ProfileMasonryPlaceholder />;
    }

    return (
      <div className="py-4">
        <PostPlaceholder />
      </div>
    );
  }

  if (error) {
    return <ErrorState position="relative" message={error.message ?? null} />;
  }

  const noPosts = data.postPage.items.length === 0;

  if (!about && noPosts) {
    return (
      <Container className="py-4">
        <EmptyPostPage position="relative" />
      </Container>
    );
  }

  const waypointBottomOffset = gridMode ? -300 : -1000;
  const disableGutters = gridMode && !noPosts;

  return (
    <MasonryOpenSeaAssetsProvider>
      {!desktopMode && about && (
        <Container>
          <div className="mt-5">{about}</div>
        </Container>
      )}

      <Container className={disableGutters ? "px-0 py-4" : "px-4 py-4"}>
        {isProfileOwner && noPosts && (
          <div className="block md:hidden">
            <EmptyPostPlaceholder />
          </div>
        )}

        <Masonry<ImageCardProps>
          key={masonryKey}
          style={{ outline: "none" }}
          items={
            desktopMode && !!about
              ? [about as any, ...targetDataStructure]
              : targetDataStructure
          }
          onRender={onRender}
          scrollToIndex={scrollToIndex}
          {...settings}
          render={({ data: item, index }) => {
            if (desktopMode && about && index === 0) return <>{about}</>;

            if (item.post === null) {
              return (
                <div className="hidden md:block">
                  <EmptyPostPlaceholder />
                </div>
              );
            }

            if (gridMode) {
              return (
                <Square>
                  <div
                    data-test-id="InfiniteMasonry-squareInMobile"
                    onClick={onClickGridItem(index)}
                    className="relative w-full h-full"
                  >
                    <div className="relative h-full">
                      <span className="absolute w-full h-full bg-gray-200" />
                    </div>
                    <Image
                      alt={item.post.title}
                      src={item.post.previewImage?.url}
                      layout="fill"
                      objectFit="cover"
                      objectPosition="center"
                      priority={true}
                    />
                  </div>
                </Square>
              );
            }

            return (
              <PostCard
                {...item}
                isRender={
                  index >= renderIndex?.startIndex &&
                  index <= renderIndex?.stopIndex
                }
              />
            );
          }}
        />

        <Waypoint
          onEnter={waypointLoadMore}
          bottomOffset={waypointBottomOffset}
          key={data?.postPage.paging.next}
        />

        {/* @todo replacing with skeleton */}
        {fetchMoreLoading && (
          <div className="mt-auto justify-center py-2">
            <Loading position="relative" />
          </div>
        )}
      </Container>
    </MasonryOpenSeaAssetsProvider>
  );
};
